import { useMe } from "@/api";
import { isHidden } from "@/lib/hidden-features";
import { useState, useEffect, useRef } from "react";
import {
  usePaths,
  useBookmarkedSessions,
  useUpdateSession,
} from "@/api";
import {
  Image,
  Card,
  Group,
  Stack,
  Text,
  useMantineTheme,
  useMantineColorScheme,
  Modal,
  Box,
  ActionIcon,
  Notification,
  Rating,
  Flex,
  Divider,
  Button,
  Timeline,
  ThemeIcon,
  Badge,
  Tabs,
  Slider,
} from "@mantine/core";
import { 
  BookmarkSimpleIcon, 
  CalendarBlankIcon, 
  XIcon,
  CaretRightIcon,
  SpeakerHighIcon,
  ArrowCounterClockwiseIcon,
  ArrowClockwiseIcon,
  PlayIcon,
  PauseIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";


const titleStyles = {
  color: "#144378",
  fontSize: "16px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  display: "flex",
  alignItems: "center",
  whiteSpace: "nowrap",
  overflow: "visible",
  textOverflow: "unset",
  wordWrap: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  maxWidth: "none",
  margin: "0px 0px 16px",
  lineHeight: "1.5",
  width: "202.72px",
  height: "24.8px",
};

const StyledTitle = ({ children, ...props }: any) => (
  <Text fw={700} size="md" style={titleStyles} {...props}>
    {children}
  </Text>
);

export default function Completed() {
  const { data: me } = useMe();
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  const { data: completedDataList } = usePaths({
    status: "completed",
  });

  const [bookmarkModalOpen, setBookmarkModalOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState<any>(null);
  const [selectedBookmarkSession, setSelectedBookmarkSession] = useState<any>(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [openPath, setOpenPath] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>("active");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const { data: bookmarkList } =
    useBookmarkedSessions(Number(selectedPath?.id) ?? 0, {
      query: { enabled: !!selectedPath?.id },
    });

  const { mutateAsync: updatePathSession } = useUpdateSession();

  const { refetch: refetchBookmarkList } =
    useBookmarkedSessions(Number(openPath) ?? 0, {
      query: {
        queryKey: ["bookmark"],
      },
    });

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener("loadedmetadata", () => {
        setDuration(audioRef.current?.duration || 0);
      });

      audioRef.current.addEventListener("timeupdate", () => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      });

      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
    }
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(
        audioRef.current.currentTime + 10,
        audioRef.current.duration
      );
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        audioRef.current.currentTime - 10,
        0
      );
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleBookmarkClick = (path: any) => {
    setSelectedPath(path);
    setSelectedBookmarkSession(null);
  };

  const handleModalClose = () => {
    setBookmarkModalOpen(false);
    setSelectedPath(null);
    setSelectedBookmarkSession(null);
  };

  useEffect(() => {
    if (selectedPath && bookmarkList !== undefined) {
      const sessionsArray = Array.isArray(bookmarkList) ? bookmarkList : bookmarkList ? [bookmarkList] : [];
      if (sessionsArray.length > 0) {
        const groupedSessions = sessionsArray.reduce((acc: any, session: any) => {
          const sessionTitle = session.title || session.session_title || "Untitled Session";
          if (!acc[sessionTitle]) {
            acc[sessionTitle] = {
              id: session.id,
              title: sessionTitle,
              recommendations: []
            };
          }
          
          if (session.recommendations && session.recommendations.length > 0) {
            acc[sessionTitle].recommendations.push(...session.recommendations.map((rec: any) => ({
              ...rec,
              session_id: session.id,
              session_title: sessionTitle
            })));
          }
          
          return acc;
        }, {});
        
        setSelectedBookmarkSession({
          id: selectedPath.id,
          title: selectedPath.name,
          status: selectedPath.status,
          groupedSessions: Object.values(groupedSessions)
        });
        setBookmarkModalOpen(true);
      } else {
        setSuccessMessage("No bookmarked sessions found for this path");
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 2000);
      }
    }
  }, [selectedPath, bookmarkList]);

  const handleRatingChange = async (recommendation: any, newRating: number) => {
    try {
      setSelectedBookmarkSession((prev: { groupedSessions: any[]; }) => ({
        ...prev,
        groupedSessions: prev?.groupedSessions?.map((session: { recommendations: any[]; }) => ({
          ...session,
          recommendations: session.recommendations.map((rec: { prompt_id: any; }) => 
            rec.prompt_id === recommendation.prompt_id 
              ? { ...rec, rating: newRating }
              : rec
          )
        }))
      }));
      
      await updatePathSession({
        id: recommendation.session_id || selectedBookmarkSession.id,
        data: {
          recommendations: selectedBookmarkSession?.groupedSessions?.flatMap((s: any) => s.recommendations).map((rec: any) => {
            if (rec.prompt_id === recommendation.prompt_id) {
              return { ...rec, rating: newRating };
            }
            return rec;
          }),
        },
      });
      
      setSuccessMessage("Rating updated successfully");
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 2000);
    } catch (error) {
      console.error("Error updating rating:", error);
      setSuccessMessage("Failed to update rating");
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 2000);
    }
  };

  const handleBookmarkToggle = async (recommendation: any) => {
    try {
      const newBookmarkValue = !recommendation?.is_bookmarked;
      
      setSelectedBookmarkSession((prev: { groupedSessions: any[]; }) => ({
        ...prev,
        groupedSessions: prev?.groupedSessions?.map((session: { recommendations: any[]; }) => ({
          ...session,
          recommendations: session.recommendations.map((rec: { prompt_id: any; }) => 
            rec.prompt_id === recommendation.prompt_id 
              ? { ...rec, is_bookmarked: newBookmarkValue }
              : rec
          )
        }))
      }));
      
      await updatePathSession({
        id: recommendation.session_id || selectedBookmarkSession.id,
        data: {
          recommendations: selectedBookmarkSession?.groupedSessions?.flatMap((s: any) => s.recommendations).map((rec: any) => {
            if (rec.prompt_id === recommendation.prompt_id) {
              return { ...rec, is_bookmarked: newBookmarkValue };
            }
            return rec;
          }),
        },
      });
      
      setSuccessMessage(newBookmarkValue ? "Bookmarked successfully" : "Removed from bookmarks");
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 2000);
    } catch (err: any) {
      console.error("update path session Error: ", err);
      setSuccessMessage("Failed to update bookmark");
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 2000);
    }
  };

  return (
    <Box>
      <Stack>
        {completedDataList?.results?.map((path) => {
          const isOpen = openPath === String(path.id);
          return (
            <Card 
              key={path.id} 
              withBorder 
              bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.orange[0]} 
              radius="md" 
              p="16px" 
              mb="md"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenPath(isOpen ? null : String(path.id))}
            >
              <Group justify="space-between" align="start">
                <Stack gap="md" w="100%">
                  <Box style={{ position: "relative" }}>
                    <Group justify="space-between" align="flex-start">
                      <Stack gap="xs" align="flex-start" style={{ flex: 1, minWidth: 0, paddingRight: 60 }}>
                        <Group gap="xs" align="center">
                          <Image
                            src={"/assets/completed.png"}
                            h={24}
                            w={24}
                            fit="contain"
                          />
                          <Text
                            c={
                              path?.status === "new"
                                ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[8]
                                : path?.status === "in_progress"
                                  ? colorScheme === "dark" ? "#FA7D87" : theme.colors.red[8]
                                  : colorScheme === "dark" ? "#4B9BE5" : theme.colors.indigo[8]
                            }
                            variant="subtle"
                            fw={500}
                            style={{
                              width: "89.83px",
                              height: "24.8px",
                              display: "flex",
                              alignItems: "center",
                              fontSize: "16px",
                              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
                            }}
                          >
                            {path?.status === "completed" ? "Completed" : 
                             path?.status === "in_progress" ? "In Progress" : 
                             path?.status === "new" ? "Not started" : 
                             path?.status || "Completed"}
                          </Text>
                        </Group>
                        
                        <Text 
                          fw={500}
                          style={{
                            fontSize: "18px",
                            color: colorScheme === "dark" ? "white" : "#393939",
                            lineHeight: "120%",
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                          mt="xs"
                        >
                          {path?.name ?? ""}
                        </Text>
                        
                        {path?.description && (
                          <Text 
                            size="sm" 
                            c={colorScheme === "dark" ? theme.colors.neutral[3] : theme.colors.neutral[7]}
                            style={{ 
                              lineHeight: 1.5,
                              marginTop: "4px",
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                            }}
                          >
                            {path?.description}
                          </Text>
                        )}
                      </Stack>
                    </Group>

                    <Box
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                      }}
                    >
                      <BookmarkSimpleIcon
                        size={40}
                        color="white"
                        style={{
                          backgroundColor: theme.colors.primary[6],
                          borderRadius: "50%",
                          padding: 8,
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBookmarkClick(path);
                        }}
                      />
                    </Box>
                  </Box>

                  <Group gap={4} justify="flex-end" mt="md">
                    <CalendarBlankIcon size={20} color={colorScheme==="dark" ? theme.colors.neutral[4] : theme.colors.neutral[8]} />
                    <Text size="sm" c={colorScheme==="dark" ? theme.colors.neutral[4] : theme.colors.neutral[8]}>
                      Completed on{" "}
                      {path?.end_date
                        ? dayjs(path?.end_date).format("MMMM D, YYYY")
                        : ""}
                    </Text>
                  </Group>

                  {/* Path Progress Details - Show when expanded */}
                  {isOpen && (
                    <Card
                      mt="lg"
                      bg={
                        colorScheme === "dark"
                          ? theme.colors.neutral[9]
                          : theme.colors.neutral[1]
                      }
                      withBorder
                      radius="md"
                      p={"xs"}
                    >
                      <Tabs
                        value={activeTab}
                        onChange={setActiveTab}
                        styles={{ tab: { fontSize: 16, padding: "12px 20px" } }}
                      >
                        <Tabs.Panel value="active" pt="xs">
                          <Stack mt="md">
                            <Group>
                              <Text fw={600} c={"gray.6"}>
                                Path Progress
                              </Text>
                              {!isHidden("listen") && (
                                <Button
                                  leftSection={<SpeakerHighIcon size={16} />}
                                  ml="auto"
                                  size="xs"
                                  variant="filled"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAudioPlayer(!showAudioPlayer);
                                  }}
                                >
                                  Listen
                                </Button>
                              )}
                            </Group>

                            {showAudioPlayer && (
                              <Card withBorder radius="md" p="md" mt="md">
                                <Group justify="space-between" mb="md">
                                  <Text fw={600} size="lg">
                                    Listen your Path
                                  </Text>
                                  <ActionIcon
                                    variant="subtle"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowAudioPlayer(false);
                                    }}
                                  >
                                    <XIcon size={20} />
                                  </ActionIcon>
                                </Group>

                                <Box
                                  p="xs"
                                  style={{
                                    background: `linear-gradient(90deg, ${theme.colors.indigo[8]} 0%, ${theme.colors.negative[4]} 100%)`,
                                    borderRadius: theme.radius.md,
                                    color: "white",
                                  }}
                                >
                                  <Group align="center" gap="xs">
                                    <ActionIcon
                                      size="lg"
                                      variant="subtle"
                                      color="white"
                                      onClick={skipBackward}
                                    >
                                      <ArrowCounterClockwiseIcon size={24} />
                                    </ActionIcon>

                                    <ActionIcon
                                      radius="xl"
                                      size="xl"
                                      variant="filled"
                                      color="white"
                                      style={{
                                        backgroundColor: "white",
                                        color: theme.colors.indigo[8],
                                      }}
                                      onClick={togglePlay}
                                    >
                                      {isPlaying ? (
                                        <PauseIcon size={24} weight="fill" />
                                      ) : (
                                        <PlayIcon size={24} weight="fill" />
                                      )}
                                    </ActionIcon>

                                    <ActionIcon
                                      size="lg"
                                      variant="subtle"
                                      color="white"
                                      onClick={skipForward}
                                    >
                                      <ArrowClockwiseIcon size={24} />
                                    </ActionIcon>
                                    <Text
                                      size="sm"
                                      c={theme.colors.neutral[2]}
                                      style={{ minWidth: 45 }}
                                    >
                                      {formatTime(duration)}
                                    </Text>

                                    <Slider
                                      value={currentTime}
                                      onChange={(value) => {
                                        if (audioRef.current) {
                                          audioRef.current.currentTime = value;
                                        }
                                      }}
                                      max={duration}
                                      min={0}
                                      step={0.1}
                                      style={{ flex: 1 }}
                                      size="sm"
                                      color="white"
                                      styles={{
                                        track: {
                                          backgroundColor: theme.colors.blue[5],
                                          border: "none",
                                        },
                                        bar: {
                                          background: theme.colors.blue[5],
                                        },
                                        thumb: {
                                          boxSizing: "content-box",
                                        },
                                      }}
                                    />
                                  </Group>
                                </Box>
                              </Card>
                            )}

                            <Timeline
                              active={(() => {
                                const sessions = path?.path_sessions || [];    
                                let lastActiveIndex = -1;
                                for (let i = 0; i < sessions.length; i++) {
                                  if (sessions[i]?.status === "completed" || sessions[i]?.status === "in_progress") {
                                    lastActiveIndex = i;
                                  }
                                }                       
                                return lastActiveIndex + 1;
                              })()}
                              bulletSize={24}
                              lineWidth={2}
                              ml={"xs"}
                            >
                              {path?.path_sessions?.map((list: any, i: number) => (
                                <Timeline.Item
                                  style={{ cursor: "pointer" }}
                                  key={i}
                                  bullet={
                                    <ThemeIcon
                                      variant="filled"
                                      color={
                                        list?.status === "completed" ? "green" : 
                                        list?.status === "in_progress" ? "yellow" : "blue"
                                      }
                                      radius="xl"
                                      size="md"
                                    >
                                      <Text size="sm">{i + 1}</Text>
                                    </ThemeIcon>
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSession(list);
                                    setModalOpen(true);
                                  }}
                                >
                                  <Group
                                    justify="space-between"
                                    align="center"
                                    mb={4}
                                  >
                                    <Group>
                                      <Text fw={600}>{list?.title}</Text>
                                      <Badge 
                                        variant="filled"
                                        style={{
                                          backgroundColor: colorScheme === "dark" ? "#727272" : "#E3EDFB",
                                          color: colorScheme === "dark" ? "#FAFAFA" : "#124B8C"
                                        }}
                                      >
                                        {list?.status === "completed" ? "COMPLETED" : 
                                         list?.status === "in_progress" ? "IN_PROGRESS" : 
                                         list?.status === "new" ? "New" : 
                                         list?.status ?? ""}
                                      </Badge>
                                    </Group>

                                    {list?.status === "current" && (
                                      <Group gap="xs">
                                        <Button variant="outline" size="xs">
                                          Complete Now
                                        </Button>
                                        <CaretRightIcon size={16} />
                                      </Group>
                                    )}
                                  </Group>
                                  <Text size="sm" c="dimmed">
                                    {list?.description ?? ""}
                                  </Text>
                                </Timeline.Item>
                              ))}
                            </Timeline>
                          </Stack>
                        </Tabs.Panel>
                      </Tabs>
                    </Card>
                  )}
                </Stack>
              </Group>
            </Card>
          );
        })}
      </Stack>

      {/* Path Progress Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        withCloseButton={false}
        centered
        size="xl"
        styles={{
          body: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          content: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          header: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
        }}
      >
        {selectedSession && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: 500,
              overflowX: "hidden",
              maxWidth: "100%",
              wordWrap: "break-word",
            }}
          >
            {showSuccessNotification && (
              <Notification
                color="teal"
                mt="md"
                onClose={() => setShowSuccessNotification(false)}
              >
                {successMessage}
              </Notification>
            )}

            <Group justify="space-between" align="center" mb="md">
              <Text
                fw={600}
                size="md"
                style={{
                  color: theme.colors.primary[8],
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                }}
              >
                {selectedSession?.title || "Session Details"}
              </Text>
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setModalOpen(false)}
                  aria-label="Close"
                >
                  <XIcon size={22} />
                </ActionIcon>
              </Group>
            </Group>

            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                maxWidth: "100%",
                wordWrap: "break-word",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <Flex direction={"column"} gap="xs" mb="md">
                {selectedSession.recommendations?.map((recommendation: any) => (
                  <Flex
                    key={recommendation?.prompt_id}
                    id={recommendation?.prompt_id}
                    direction={"column"}
                  >
                    <Flex w={"100%"} justify={"end"}>
                      <Rating
                        defaultValue={recommendation?.rating}
                        fractions={2}
                        size="md"
                        onChange={async (newRating) => {
                          updatePathSession({
                            id: selectedSession.id,
                            data: {
                              recommendations:
                                selectedSession?.recommendations?.map((rec: any) => {
                                  if (
                                    rec.prompt_id === recommendation.prompt_id
                                  ) {
                                    return { ...rec, rating: newRating };
                                  }
                                  return rec;
                                }),
                            },
                          }).then(() => {
                            setSuccessMessage("Rating updated successfully");
                            setShowSuccessNotification(true);
                            setTimeout(
                              () => setShowSuccessNotification(false),
                              2000
                            );
                            refetchBookmarkList();
                          });
                        }}
                      />
                      <BookmarkSimpleIcon
                        size={22}
                        color={theme.colors.neutral[8]}
                        key={`${recommendation?.prompt_id}-${recommendation?.is_bookmarked}`}
                        weight={
                          recommendation?.is_bookmarked ? "fill" : "regular"
                        }
                        style={{ cursor: "pointer" }}
                        onClick={async (_value: any) => {
                          try {
                            const newBookmarkValue =
                              !recommendation?.is_bookmarked;
                              updatePathSession({
                                id: selectedSession.id,
                                data: {
                                  recommendations:
                                    selectedSession?.recommendations?.map((rec: any) => {
                                      if (
                                        rec.prompt_id === recommendation.prompt_id
                                      ) {
                                        return { ...rec, is_bookmarked: newBookmarkValue };
                                      }
                                      return rec;
                                    }),
                                },
                              }).then(()=>{
                                setSuccessMessage("Bookmark updated successfully");
                              setShowSuccessNotification(true);
                            setSelectedSession({
                              ...selectedSession,
                              is_bookmarked: newBookmarkValue,
                              recommendations:
                                selectedSession?.recommendations?.map((rec: any) => {
                                  if (
                                    rec.prompt_id === recommendation.prompt_id
                                  ) {
                                    return {
                                      ...rec,
                                      is_bookmarked: newBookmarkValue,
                                    };
                                  }
                                  return rec;
                                }),
                              });
                              
                            setTimeout(
                              () => setShowSuccessNotification(false),
                              2000
                            );
                            refetchBookmarkList();
                              })
                              
                          } catch (err: any) {
                            console.error("update path session Error: ", err);
                          }
                        }}
                      />
                    </Flex>
                    <Text
                      size="sm"
                      style={{
                        lineHeight: 1.6,
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-wrap",
                        maxWidth: "100%",
                      }}
                    >
                      {(recommendation?.prompt_text as string)?.replaceAll(
                        "<user_preferred_name>",
                        me?.first_name ?? "User"
                      ).replaceAll("||", "")
                      .replaceAll("{UserPreferredName}", me?.first_name ?? "User")}
                    </Text>
                    <Divider orientation="horizontal" />
                  </Flex>
                ))}
              </Flex>
            </Box>
          </Box>
        )}
      </Modal>

      <Modal
        opened={bookmarkModalOpen}
        onClose={handleModalClose}
        withCloseButton={false}
        centered
        size="xl"
        styles={{
          body: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          content: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
          header: {
            overflowX: "hidden",
            maxWidth: "100%",
            wordWrap: "break-word",
          },
        }}
      >
        {selectedBookmarkSession && (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              height: 500,
              overflowX: "hidden",
              maxWidth: "100%",
              wordWrap: "break-word",
            }}
          >
            {showSuccessNotification && (
              <Notification
                color="teal"
                mb="md"
                onClose={() => setShowSuccessNotification(false)}
                style={{
                  position: "relative",
                  zIndex: 1000,
                }}
              >
                {successMessage}
              </Notification>
            )}

            <Group justify="space-between" align="center" mb="md">
              <Box>
                <StyledTitle>
                  {selectedBookmarkSession?.title || "Bookmarked Session"}
                </StyledTitle>
              </Box>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={handleModalClose}
                aria-label="Close"
              >
                <XIcon size={22} />
              </ActionIcon>
            </Group>

            <Box
              style={{
                flex: 1,
                overflowY: "auto",
                maxWidth: "100%",
                wordWrap: "break-word",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {selectedBookmarkSession.groupedSessions && selectedBookmarkSession.groupedSessions.length > 0 ? (
                <Stack gap="lg">
                  {selectedBookmarkSession.groupedSessions.map((session: any, sessionIndex: number) => (
                    <Box key={session.id || sessionIndex}>
                      <StyledTitle>
                        {session.title}
                      </StyledTitle>
                      
                      {session.recommendations && session.recommendations.length > 0 ? (
                        <Stack gap="md">
                          {session.recommendations.map((recommendation: any, recIndex: number) => (
                            <Box key={`${recommendation?.prompt_id || recIndex}`}>
                              <Flex w="100%" justify="end" mb="sm">
                                <Rating
                                  defaultValue={recommendation?.rating || 0}
                                  fractions={2}
                                  size="md"
                                  onChange={(newRating) => handleRatingChange(recommendation, newRating)}
                                />
                                <BookmarkSimpleIcon
                                  size={22}
                                  color={theme.colors.neutral[8]}
                                  weight={recommendation?.is_bookmarked ? "fill" : "regular"}
                                  style={{ cursor: "pointer", marginLeft: 8 }}
                                  onClick={() => handleBookmarkToggle(recommendation)}
                                />
                              </Flex>
                              
                              <Text
                                size="sm"
                                style={{
                                  lineHeight: 1.6,
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  whiteSpace: "pre-wrap",
                                  maxWidth: "100%",
                                }}
                              >
                                {(recommendation?.prompt_text as string)?.replace(
                                  /<user_preferred_name>/g,
                                  me?.first_name ?? "User"
                                ).replaceAll("||", "")
                                .replaceAll("{UserPreferredName}", me?.first_name ?? "User")}
                              </Text>
                              
                              {recIndex < session.recommendations.length - 1 && (
                                <Divider orientation="horizontal" mt="md" mb="md" />
                              )}
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Text c="dimmed" size="sm">
                          No recommendations found for this session.
                        </Text>
                      )}
                      
                      {sessionIndex < selectedBookmarkSession.groupedSessions.length - 1 && (
                        <Divider orientation="horizontal" mt="lg" mb="lg" />
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No bookmarked sessions found for this path.
                </Text>
              )}
            </Box>
          </Box>
        )}
      </Modal>

      {/* No audio source until a real text-to-speech service exists (hidden-features.listen). */}
      <audio ref={audioRef} preload="none" />
    </Box>
  );
}
